import { createContext, useState } from "react";
import UIkit from "uikit";

import WorkSchedulesService from "../services/work_schedules.service";

export const WorkSchedulesContext = createContext();

export const WorkSchedulesProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsCount, setRecipientsCount] = useState(0);
  const [holidays, setHolidays] = useState([]);
  // Incremented whenever a scheduling change occurs; consumers watch this to refresh
  const [scheduleChangeSignal, setScheduleChangeSignal] = useState({ date: null, version: 0 });
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: 20,
  });

  const contextWorkSchedules = {
    loading,
    isProcessing,
    error,
    recipients,
    recipientsCount,
    holidays,
    pagination,
    scheduleChangeSignal,

    
    /* Call after any scheduling mutation (create/update/delete schedule, assign WO, etc.)
     to notify all availability consumers to refresh the affected date. */
    notifyScheduleChange: (date) => {
      setScheduleChangeSignal((prev) => ({ date, version: prev.version + 1 }));
    },

    // Helper functions for pagination control
    setPage: (page) => setPagination((prev) => ({ ...prev, page })),
    setPerPage: (per_page) => setPagination((prev) => ({ ...prev, per_page, page: 0 })),

    getRecipientsWithSchedules: async (criteria = {}) => {
      setLoading(true);
      setError(null);
      try {
        // Merge criteria with current pagination state
        const params = {
          page: criteria.page ?? pagination.page,
          per_page: criteria.per_page ?? pagination.per_page,
          ...criteria,
        };

        const response = await WorkSchedulesService.listRecipientsWithSchedules(params);
        setRecipients(response.data?.recipients || []);
        setRecipientsCount(response.data?.total_rows || 0);
        
        // Update pagination state with the values used in the request
        setPagination({
          page: params.page,
          per_page: params.per_page,
        });

        setLoading(false);
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Failed to load work schedules";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        setLoading(false);
        throw e;
      }
    },

    createSchedules: async (recipient_id, schedules) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await WorkSchedulesService.createSchedules(recipient_id, schedules);
        if (!response.error) {
          UIkit.notification("Work schedule created successfully", { status: "success" });
          
          // Refetch current page to get updated data
          await contextWorkSchedules.getRecipientsWithSchedules({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error creating work schedule";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.error || "Error creating work schedule";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    updateSchedules: async (recipient_id, schedules) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await WorkSchedulesService.updateSchedules(recipient_id, schedules);
        if (!response.error) {
          UIkit.notification("Work schedule updated successfully", { status: "success" });
          
          // Refetch current page to get updated data
          await contextWorkSchedules.getRecipientsWithSchedules({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error updating work schedule";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.error || "Error updating work schedule";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    deleteSchedules: async (recipient_id) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await WorkSchedulesService.deleteSchedules(recipient_id);
        if (!response.error) {
          UIkit.notification("Work schedule deleted successfully", { status: "success" });
          
          // Refetch current page to get updated data
          await contextWorkSchedules.getRecipientsWithSchedules({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error deleting work schedule";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Error deleting work schedule";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    createException: async (exceptionData) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await WorkSchedulesService.createException(exceptionData);
        if (!response.error) {
          UIkit.notification("Exception created successfully", { status: "success" });
          
          // Refetch current page to refresh future_exceptions
          await contextWorkSchedules.getRecipientsWithSchedules({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error creating exception";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.error || "Error creating exception";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    updateException: async (exception_id, exceptionData) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await WorkSchedulesService.updateException(exception_id, exceptionData);
        if (!response.error) {
          UIkit.notification("Exception updated successfully", { status: "success" });
          
          // Refetch current page to refresh future_exceptions
          await contextWorkSchedules.getRecipientsWithSchedules({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error updating exception";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.error || "Error updating exception";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    deleteException: async (exception_id) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await WorkSchedulesService.deleteException(exception_id);
        if (!response.error) {
          UIkit.notification("Exception deleted successfully", { status: "success" });
          
          // Refetch current page to refresh future_exceptions
          await contextWorkSchedules.getRecipientsWithSchedules({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error deleting exception";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Error deleting exception";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    getAvailability: async (start_date, end_date, limit = 100, offset = 0) => {
      setLoading(true);
      setError(null);
      try {
        const response = await WorkSchedulesService.getAvailability(start_date, end_date, limit, offset);
        setLoading(false);
        return response.data || [];
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Failed to load availability";
        setError(errorMessage);
        console.error("Error loading availability:", errorMessage);
        setLoading(false);
        return [];
      }
    },

    getHolidays: async (country_code = 'US', date_from = null, date_to = null) => {
      try {
        const response = await WorkSchedulesService.getHolidays(
          country_code,
          date_from,
          date_to
        );
        setHolidays(response.data?.holidays || []);
        return response.data?.holidays || [];
      } catch (e) {
        console.error("Failed to load holidays:", e);
        setHolidays([]);
        return [];
      }
    },
  };

  return (
    <WorkSchedulesContext.Provider value={contextWorkSchedules}>
      {children}
    </WorkSchedulesContext.Provider>
  );
};
